import { cn } from "@/lib";
import { useAtomValue } from "jotai";
import { blockPresetsAtom } from "@/components/TemplateEditor/store";

interface DropIndicatorPlaceholderProps {
  type?: string | null;
}

/**
 * Built-in block type labels
 */
const BLOCK_LABELS: Record<string, string> = {
  text: "Text",
  heading: "Heading",
  spacer: "Spacer",
  divider: "Divider",
  button: "Button",
  image: "Image",
  list: "List",
  customCode: "HTML",
  column: "Column layout",
  blockquote: "Blockquote",
};

/**
 * Check if a type string is a preset reference (format: "blockType:presetKey")
 */
const isPresetType = (type: string): boolean => {
  return type.includes(":");
};

/**
 * Parse a preset type string into its components
 */
const parsePresetType = (type: string): { blockType: string; presetKey: string } | null => {
  const parts = type.split(":");
  if (parts.length === 2) {
    return { blockType: parts[0], presetKey: parts[1] };
  }
  return null;
};

export const DropIndicatorPlaceholder: React.FC<DropIndicatorPlaceholderProps> = ({ type }) => {
  const presets = useAtomValue(blockPresetsAtom);

  const getPlaceholderLabel = (type?: string | null): string => {
    if (!type) return "Drop here";

    // Check if it's a preset reference (format: "blockType:presetKey")
    if (isPresetType(type)) {
      const parsed = parsePresetType(type);
      if (parsed) {
        // Find the preset and use its label
        const preset = presets.find(
          (p) => p.type === parsed.blockType && p.key === parsed.presetKey
        );
        if (preset) {
          return preset.label;
        }
      }
      // Fallback: if preset not found, try to use the block type label
      const blockType = type.split(":")[0];
      return BLOCK_LABELS[blockType] || "Drop here";
    }

    // Built-in block type
    return BLOCK_LABELS[type] || "Drop here";
  };

  return (
    <div className="courier-flex courier-w-full courier-pointer-events-none">
      {/* `courier-ml-10` matches the handle row's `courier-pl-10`, which this is a
          sibling of rather than inside — so without it the indicator sits 40px
          left of the block content it is supposed to line up between. The email
          canvas zeroes both for top-level blocks (see the drag-gutter block in
          `styles.css`), where the handle lives in a real gutter instead.
          `pointer-events-none` above is load-bearing during a drag: hit testing
          falls through to the wrapper, so the drop target is not lost when the
          pointer crosses the indicator. */}
      <div
        className={cn(
          "courier-relative courier-flex courier-flex-grow courier-items-center courier-px-5 courier-py-[18px] courier-bg-background/50 courier-ml-10",
          "courier-border-2 courier-border-dashed courier-border-accent-foreground courier-rounded-md courier-drag-indicator dark:courier-border-accent"
        )}
      >
        <div className="courier-text-accent-foreground dark:courier-text-accent">
          {getPlaceholderLabel(type)}
        </div>
      </div>
    </div>
  );
};
